/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { isJsdFooterOutsideMainLandmarkEnabled } from '@helpcenter/feature-flags';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { MainContentSkipLinkWrapper } from '../../common/skip-links';
import { StyledFooter, NoteBackground, NoteContent } from '../paper/styled';
import type { GridAppearanceTypes } from './utils';
import { getContentWidth } from './utils';

interface SkipToMainContentProps {
	children: React.ReactNode;
}

export const SkipToMainContent = ({ children }: SkipToMainContentProps) => (
	<MainContentSkipLinkWrapper>
		{/* Upgrading @atlaskit/platform; fix these violations. */}
		{/* eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-registration */}
		{fg('do_not_use_banner_colour_for_hyperlinks') ? (
			<Box testId="skip-to-main-content" xcss={mainContentSkipLinkStyles}>
				<div css={wrapperStyles}>{children}</div>
			</Box>
		) : (
			<Box testId="skip-to-main-content" xcss={mainContentSkipLinkStyles}>
				{children}
			</Box>
		)}
	</MainContentSkipLinkWrapper>
);

export interface Props {
	children: React.ReactNode;
	appearance?: GridAppearanceTypes;
	isCSM?: boolean;
	hideSkipToMainContent?: boolean;
}

interface GridContentWrapperProps {
	children: JSX.Element;
	appearance: GridAppearanceTypes;
	hide: boolean;
}

export const GridContentWrapper: React.FC<GridContentWrapperProps> = ({
	children,
	appearance,
	hide,
}) => {
	const gridWidth = getContentWidth(appearance);

	return (
		<Box
			testId="grid-content-wrapper"
			xcss={staticWrapperStyles}
			style={{ maxWidth: hide ? '100%' : gridWidth }}
		>
			{children}
		</Box>
	);
};

const GridContainer = ({
	children,
	isCSM,
	hideSkipToMainContent,
	appearance: contentAppearance,
}: Props) => {
	const hide = contentAppearance === 'none';
	const appearance = contentAppearance || 'normal';

	// A11Y-29321: when enabled, the footer is rendered outside the `<main>` landmark (WCAG 1.3.1).
	// `margin-top: auto` keeps it pinned to the bottom of the flex column.
	const footerOutsideMain = isJsdFooterOutsideMainLandmarkEnabled();

	if (appearance === 'note') {
		const noteContent = (
			<NoteContent appearance={appearance} hide={hide} data-testid="note-content">
				<NoteBackground data-testid="note-background">{children}</NoteBackground>
				{!footerOutsideMain && <StyledFooter />}
			</NoteContent>
		);

		return (
			<Box xcss={noteContainerStyles}>
				{footerOutsideMain ? (
					// Footer rendered outside the `<main>` note card, pinned to the bottom.
					<Box xcss={noteColumnStyles}>
						{noteContent}
						<Box xcss={footerAtBottomStyles}>
							<StyledFooter />
						</Box>
					</Box>
				) : (
					noteContent
				)}
			</Box>
		);
	}

	const footer = <StyledFooter isCSM={isCSM} />;

	const mainAndFooter = footerOutsideMain ? (
		// Footer rendered as a sibling of the `<main>` landmark (outside it), pinned to the bottom.
		<Box xcss={mainColumnStyles}>
			<SkipToMainContent>{children}</SkipToMainContent>
			<Box xcss={footerAtBottomStyles}>{footer}</Box>
		</Box>
	) : (
		// Legacy behaviour: the footer is rendered inside the `<main>` landmark.
		<SkipToMainContent>
			{children}
			{footer}
		</SkipToMainContent>
	);

	return (
		<Box as="section" xcss={sectionStyles}>
			<GridContentWrapper appearance={appearance} hide={hide}>
				<Grid xcss={gridContainerStyles} justifyContent="center">
					<Grid xcss={gridItemStyles}>
						{hideSkipToMainContent ? (
							<>
								{children}
								{footer}
							</>
						) : (
							mainAndFooter
						)}
					</Grid>
				</Grid>
			</GridContentWrapper>
		</Box>
	);
};

const wrapperStyles = css({
	// Nested selectors will be addressed in REQIN-2473
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		color: `${token('color.link', '#2684FF')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		color: `${token('color.link', '#2684FF')} !important`,
	},
});

const sectionStyles = xcss({
	flex: 1,
});

const staticWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '100%',
	margin: '0 auto',
});

const gridContainerStyles = xcss({
	flex: 1,
	gridTemplateColumns: 'repeat(12, 1fr)',
});

const gridItemStyles = xcss({
	gridColumn: 'span 12',
});

const mainContentSkipLinkStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	minHeight: '100%',
});

// A11Y-29321: flex column holding the `<main>` landmark and the footer as siblings so the footer
// can sit outside `<main>` while staying pinned to the bottom of the page.
const mainColumnStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	minHeight: '100%',
});

const noteColumnStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	minHeight: '100%',
});

// A11Y-29321: pins the footer to the bottom of the flex column while it sits outside `<main>`.
const footerAtBottomStyles = xcss({
	marginTop: 'auto',
});

const noteContainerStyles = xcss({
	position: 'relative',
	display: 'flex',
	flex: '1 1 auto',
	overflow: 'hidden',
});

export default GridContainer;
